import ControlPointIcon from "@mui/icons-material/ControlPoint";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneIcon from '@mui/icons-material/Done';
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, Collapse, Grid, IconButton, InputAdornment, styled, Tooltip } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { useDispatch, useSelector } from "react-redux";
import * as constants from "../../../constants";
import CustomTextField from "../../../shared/CustomTextFeild";
import { setSubChartDetails } from "../../../store/Financials/saveChartSlice";
import { RootState } from "../../../store/store";
import { addMasterChartAccounts, deleteSubChartAccounts } from "../services/Financials/budget.service";
import CommonUtilities from "../Utils/CommonUtilities";
import { formatCurrency } from "../Utils/formatUtils";
import { notifyUser } from "../Utils/notifyUser";

export interface Column {
  typeEdit?: boolean;
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "left" | "center";
  format?: (value: any) => string;
}

interface ReusableTableProps {
  tableKey?: string;
  columns: Column[];
  rows: Record<string, any>[];
  chartAccountsVOList?: any[];
  loading?: boolean;
  readOnlyForecast?: boolean;
  containerClass?: string;
  forecastYear?: string;
  isExpand?: boolean;
  pageType?: string;
  type?: string;
  mainRow?: any;
  onStateRowsChange?: (updatedRows: any[]) => void;
  setToRefresh?: (value: boolean) => void;
}

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 650,
  "& .MuiTableCell-root": {
    border: "0.5px solid #f2f6fa",
  },
  borderBottom: "1px solid #dadcdc"
}));

const ReusableSubChartsAccountTable: React.FC<ReusableTableProps> = ({
  tableKey,
  columns,
  rows = [],
  chartAccountsVOList = [],
  loading = false,
  readOnlyForecast = false,
  containerClass = "container",
  forecastYear,
  isExpand,
  pageType,
  type,
  mainRow,
  onStateRowsChange,
  setToRefresh,
}) => {
  const [monthlyAmountList, setMonthlyAmountList] = useState<any[]>([]);
  const [participantId, setParticipantId] = useState(0);
  const [financialYear, setFinancialYear] = useState(0);
  const [stateRows, setStateRows] = useState<any[]>([]);
  const [stateUpdatedRows, setStateUpdatedRows] = useState<any[]>([]);
  const stateMainRow = mainRow;
  const [editingForecasts, setEditingForecasts] = useState<{ [key: string]: string }>({});
  const [editFlags, setEditFlags] = useState<{ [key: number]: boolean }>({});
  const userDetails = useSelector((state: RootState) => state.auth);
  const [accountNumber, setAccountNumber] = useState("");
  const [description, setDescription] = useState("");
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const subChartSaveVO = useSelector((state: RootState) => state.subChart);
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [isExpandFlag, setIsExpandFlag] = useState(false);

  useEffect(() => {
    if (rows) {
      setStateRows(rows);
    }
  }, []);
  useEffect(() => {
    if (onStateRowsChange) {
      onStateRowsChange(stateUpdatedRows);
    }
  }, [stateUpdatedRows, onStateRowsChange]);

  async function handleAddSubChartAccountRow(mainRow: any) {

    const hasValidationError = !accountNumber.trim() || !description.trim();

    if (hasValidationError) {
      setStateRows((prevRows) => {
        const newRows = [...prevRows];
        const lastIndex = newRows.length - 1;
        if (lastIndex >= 0) {
          newRows[lastIndex].validationError = true;
        }
        return newRows;
      });
      return;
    }

    const masterChartAccountsVO = mainRow;

    const newSubChartAccount = {
      id: 0,
      chartId: masterChartAccountsVO.id,
      accountNumber: accountNumber,
      accountName: description,
      isActive: constants.IS_ACTIVE_YES,
    };

    if (!masterChartAccountsVO.masterSubChartAccountsList) {
      masterChartAccountsVO.masterSubChartAccountsList = [];
    }

    masterChartAccountsVO.masterSubChartAccountsList.push(newSubChartAccount);
    await toAddMasterChartAccounts(masterChartAccountsVO);

    const newRow = {
      accountNumber,
      accountName: description,
      validationError: false,
    };

    setStateRows((prevRows) => [
      ...prevRows.map((r) => ({ ...r, validationError: false })),
      newRow,
    ]);

    setAccountNumber("");
    setDescription("");
  }
  useEffect(() => {
    if (isExpand) {
      setIsExpandFlag(true);
      const allIndices = rows.map((_, index) => index);
      setExpandedRows(allIndices);
      setTimeout(() => {
        setIsExpandFlag(false);
      }, 2000);
    } else {
      setExpandedRows([]);
      setIsExpandFlag(false);
    }
  }, [isExpand]);

  const handleExpandClick = (index: number, row: any) => {
    setMonthlyAmountList(row?.subChartAccountsTransactions);
    setExpandedRows(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const handleNotes = (rowId: any, newNotes: string) => {
    setStateRows((prevRows) => {
      const updatedRows = prevRows.map((row) => {
        if (row?.subChartAccountsNotesVO.forecastSubChartId === rowId.subChartAccountsNotesVO.forecastSubChartId) {
          const existingNotesVO = row.subChartAccountsNotesVO ?? {};

          const updatedNotesVO = {
            ...existingNotesVO,
            ...(pageType === "ActualsDetails" && { actualNotes: newNotes }),
            ...(pageType === "BudgetDetails" && { budgetNotes: newNotes }),
          };

          return {
            ...row,
            subChartAccountsNotesVO: updatedNotesVO,
          };
        }
        return row;
      });

      return updatedRows;
    });
  };

  const handleNotesBlur = (rowId: any, newNotes: string) => {
    setStateRows((prevRows) => {
      let editedRow: any | undefined;

      const updatedRows = prevRows.map((row) => {
        if (
          row?.subChartAccountsNotesVO?.forecastSubChartId ===
          rowId?.subChartAccountsNotesVO?.forecastSubChartId
        ) {
          const existingNotesVO = row.subChartAccountsNotesVO ?? {};

          const updatedNotesVO = {
            ...existingNotesVO,
            ...(pageType === 'ActualsDetails' && { actualNotes: newNotes }),
            ...(pageType === 'BudgetDetails' && { budgetNotes: newNotes }),
          };

          editedRow = {
            ...row,
            subChartAccountsNotesVO: updatedNotesVO,
          };

          return editedRow;
        }
        return row;
      });

      if (editedRow) {
        dispatch(setSubChartDetails(editedRow));
      }

      dispatch(setSubChartDetails(updatedRows));
      return updatedRows;
    });
  };
  const handleForecastChange = (month: string, row: { id: string }, newAmount: string) => {
    let sanitized = newAmount;
    if (sanitized.startsWith('-')) {
      sanitized = '-' + sanitized.slice(1).replace(/[^0-9.]/g, '');
    } else {
      sanitized = sanitized.replace(/[^0-9.]/g, '');
    }
    const isNegative = sanitized.startsWith('-');
    const numberStr = isNegative ? sanitized.slice(1) : sanitized;

    const [integerPart, fractionalPart] = numberStr.split('.');

    if (integerPart.length > 12) return;
    if (fractionalPart && fractionalPart.length > 2) return;

    setEditingForecasts((prev: any) => ({
      ...prev,
      [row.id]: {
        ...(prev[row.id] || {}),
        [month]: newAmount,
      },
    }));
  };

  function parseFormattedNumber(value: string | number): number {
    if (typeof value === "number") return value;
    if (typeof value !== "string") return 0;
    const clean = value.replace(/[$,]/g, "");
    const parsed = parseFloat(clean);
    return isNaN(parsed) ? 0 : parsed;
  }
  const handleForecastBlur = (month: any, _amount: string | number, rowId: number) => {
    const amount = editingForecasts[rowId]?.[month] ?? _amount;
    setStateRows((prevRows) => {
      const rowIndex = prevRows.findIndex((row) => row.id === rowId);
      if (rowIndex === -1) return prevRows;

      const updatedRows = [...prevRows];
      const currentRow = { ...updatedRows[rowIndex] };

      const transactions = currentRow.subChartAccountsTransactions
        ? currentRow.subChartAccountsTransactions.map((tx: any) => ({ ...tx }))
        : [];
      const transactionIndex = transactions.findIndex((tx: any) => tx.month === month);
      if (transactionIndex === -1) return prevRows;

      if (amount) {
        if (pageType === "BudgetDetails") {
          transactions[transactionIndex].forecastAmount = parseFormattedNumber(amount);
        } else if (pageType === "ActualsDetails") {
          transactions[transactionIndex].actualAmount = parseFormattedNumber(amount);
        }
      }

      const totalAmount = transactions.reduce(
        (sum: number, tx: any) =>
          sum + ((pageType === "BudgetDetails" ? tx.forecastAmount : tx.actualAmount) || 0),
        0
      );

      if (pageType === "BudgetDetails") {
        currentRow.curBudget = formatCurrency(totalAmount);
      } else if (pageType === "ActualsDetails") {
        if (type === "income" || type === "expenses") {
          currentRow.actualAmount = formatCurrency(totalAmount);
          const budgetAmountNum = parseFormattedNumber(currentRow.budgetAmount);
          const variance = totalAmount - budgetAmountNum;
          currentRow.varianceAmount = formatCurrency(variance);
          currentRow.fullYearBudgetRemaining = formatCurrency(variance);
          let variancePercentage = "-";
          if (budgetAmountNum !== 0) {
            variancePercentage = `${((variance / budgetAmountNum) * 100).toFixed(2)}%`;
          }
          currentRow.variancePercentage = variancePercentage;
          currentRow.percentageBudgetRemaining = variancePercentage;
        } else {
          currentRow.fullBudget = formatCurrency(totalAmount);
        }
      }

      currentRow.subChartAccountsTransactions = transactions;
      updatedRows[rowIndex] = currentRow;

      setEditingForecasts((prev: any) => {
        const newEdits = { ...prev };
        if (newEdits[rowId]?.[month]) {
          delete newEdits[rowId][month];
          if (Object.keys(newEdits[rowId]).length === 0) {
            delete newEdits[rowId];
          }
        }
        return newEdits;
      });

      dispatch(setSubChartDetails(currentRow));
      return updatedRows;
    });
  };


  const handleRowBlur = (rowId: number) => {
    const row = stateRows.find(r => r.id === rowId);
    if (row) {
      dispatch(setSubChartDetails(row));
    }
  };

  const handleRowEdit = (rowId: number, columnId: string, newValue: string) => {
    setStateRows(prevRows => {
      const rowIndex = prevRows.findIndex(row => row.id === rowId);
      if (rowIndex === -1) return prevRows;
      const sanitized = newValue.replace(/[^0-9.-]/g, "");
      const [integerPart, fractionalPart] = sanitized.replace("-", "").split(".");
      if (integerPart.length > 12) return prevRows;
      if (fractionalPart && fractionalPart.length > 2) return prevRows;

      const updatedRows = [...prevRows];
      const currentRow = { ...updatedRows[rowIndex] };

      currentRow[columnId] = newValue;
      if (columnId === "curBudget") {
        const trimmedValue = (newValue || "").toString().trim();
        const sanitizedValue = trimmedValue.replace(/[^0-9.-]/g, "");
        const curBudget = parseFloat(sanitizedValue);
        if (!isNaN(curBudget) && isFinite(curBudget)) {
          const numMonths = 12;
          const unroundedAmount = curBudget / numMonths;
          const roundedAmount = +unroundedAmount.toFixed(2);
          const first11Total = +(roundedAmount * (numMonths - 1)).toFixed(2);
          const lastAmount = +(curBudget - first11Total).toFixed(2);

          currentRow.subChartAccountsTransactions = currentRow.subChartAccountsTransactions.map((transaction: any, idx: number) => {
            const isLast = idx === 0;
            return {
              ...transaction,
              forecastAmount: isLast ? lastAmount : roundedAmount,
            };
          });
        } else {
          currentRow.subChartAccountsTransactions = currentRow.subChartAccountsTransactions.map((transaction: any) => {
            return {
              ...transaction,
              forecastAmount: 0,
            };
          });
        }
      }
      updatedRows[rowIndex] = currentRow;

      dispatch(setSubChartDetails(currentRow));

      return updatedRows;
    });
  };

  const handleSaveClick = (index: any, row: any, mainRow: any) => {
    const masterChartAccountsVO = mainRow;

    const updatedSubChartAccount = {
      ...row,
    };
    if (!masterChartAccountsVO.masterSubChartAccountsList) {
      masterChartAccountsVO.masterSubChartAccountsList = [];
    }
    const existingIndex = masterChartAccountsVO.masterSubChartAccountsList.findIndex(
      (item: any) => item.id === row.id
    );

    if (existingIndex !== -1) {
      masterChartAccountsVO.masterSubChartAccountsList[existingIndex] = updatedSubChartAccount;
    } else {
      masterChartAccountsVO.masterSubChartAccountsList.push(updatedSubChartAccount);
    }
    toAddMasterChartAccounts(masterChartAccountsVO);

    setEditFlags((prev) => ({
      ...prev,
      [index]: false,
    }));
  };


  async function toAddMasterChartAccounts(masterChartAccountsVO: any) {
    try {
      const responseList = await addMasterChartAccounts(masterChartAccountsVO);

      if (responseList?.status === constants.STATUS_CODES.SUCCESS) {
        notifyUser(responseList?.data?.message, 'success', translate('budgetAccountDetails'));
      } else {
        notifyUser(responseList?.data?.message, 'error', translate('budgetAccountDetails'));
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(translate('axiosError'), error.response?.data ?? error.message);
      } else {
        console.error(translate('unexpectedError'), error);
      }
    } finally {

      setToRefresh?.(true);
    }
  }
  const handleEditClick = (index: any, row: any) => {
    setEditingRowIndex(index);
    setEditFlags((prev) => ({
      ...prev,
      [index]: true,
    }));
  };

  async function handleDeleteClick(index: number, row: any) {
    try {
      const responseList = await deleteSubChartAccounts(row.id, userDetails.clientId);
      if (responseList?.status === constants.STATUS_CODES.SUCCESS) {
        notifyUser(responseList?.data?.message, 'success', translate('budgetAccountDetails'));
      } else {
        notifyUser(responseList?.data?.message, 'error', translate('budgetAccountDetails'));
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(translate('axiosError'), error.response?.data ?? error.message);
      } else {
        console.error(translate('unexpectedError'), error);
      }
    } finally {
      setToRefresh?.(true);
    }
  }

  const sortByCustomMonthOrder = (transactions: any) => {
    const monthOrder = CommonUtilities.monthOrderList;
    if (!Array.isArray(transactions)) return [];
    const transactionsCopy = [...transactions];
    return transactionsCopy.sort((a: any, b: any) => {
      return monthOrder.indexOf(a.month) - monthOrder.indexOf(b.month);
    });
  };
  useEffect(() => {
    setParticipantId(userDetails.defaultParticipantId);
  }, [userDetails]);

  useEffect(() => {
    if (forecastYear && forecastYear.length === 4) {
      const currentYear = parseInt(forecastYear.substring(2, 4)) + 2000;
      setFinancialYear(currentYear);
    }
  }, [forecastYear]);




  return (
    <div>
      <TableContainer sx={{ overflow: "auto" }}>
        <StyledTable
          stickyHeader
          aria-label="sticky table"
          size="small"
          sx={{
            [`& .${tableCellClasses.root}`]: {
              borderBottom: "none",
              borderTop: "none",
            },
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.id} align={column.align} className="cellTask"  >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading || (isExpandFlag && pageType !== "BudgetAccountDetails") ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <div >
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <span style={{ fontSize: "20px" }}>Loading...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : stateRows?.length === 0 && pageType !== "BudgetAccountDetails" ? (
              <TableRow>
                <TableCell align="center" colSpan={columns.length}  >
                  {translate('noRecordsFound')}
                </TableCell>
              </TableRow>
            ) : (
              stateRows?.map((row, index) => {
                const isRowExpanded = expandedRows.includes(index);
                const sortedTransactions = sortByCustomMonthOrder(row?.subChartAccountsTransactions);
                return (
                  <React.Fragment key={index}>
                    <TableRow
                      sx={{
                        backgroundColor: isExpand ? "#f1f3f4" : index % 2 === 0 ? "#f1f3f4" : "#fff",
                      }}
                    >
                      {columns.map((column) => (
                        <TableCell key={column.id} align={column.align} className="left-aligned-text" style={{ minWidth: column?.minWidth ? column.minWidth : "auto", font: '400 16px/1.2 "Inter", sans-serif' }}>
                          {column.id === "accountNumber" && pageType !== "BudgetAccountDetails" && (
                            <IconButton size="small" onClick={() => handleExpandClick(index, row)}>
                              {isRowExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                          )}
                          {column.id === "editDelete" ? (
                            <Box display="flex" gap={1} justifyContent="center"  >
                              {editingRowIndex === index && editFlags[index] ? (
                                <DoneIcon
                                  sx={{ cursor: "pointer" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSaveClick(index, row, stateMainRow ? stateMainRow : null);
                                  }}
                                />
                              ) : (
                                <EditIcon
                                  style={{ cursor: "pointer" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditClick(index, row);
                                  }}
                                />
                              )}
                              <DeleteIcon
                                sx={{ color: "error.main", cursor: "pointer" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(index, row);
                                }}
                              />
                            </Box>
                          ) : editingRowIndex === index && editFlags[index] && column.id === "accountNumber" ? (
                            <Grid container direction="row"  >
                              <Grid item lg={10}  >
                                <CustomTextField
                                  value={row[column.id]}
                                  onChange={(e) => {
                                    const inputValue = e.target.value;
                                    const isValid = /^([a-zA-Z0-9.-]*)$/.test(inputValue);
                                    if (isValid) {
                                      const updatedRows = [...stateRows];
                                      updatedRows[index][column.id] = inputValue;
                                      setStateRows(updatedRows);
                                    }
                                  }}
                                  style={{ marginLeft: 5 }}
                                  inputProps={{ maxLength: 16, inputMode: "text", pattern: "[a-zA-Z0-9.-]*" }}
                                />
                              </Grid>
                            </Grid>
                          ) : editingRowIndex === index && editFlags[index] && column.id === "accountName" ? (

                            <Grid container direction="row"  >
                              <Grid item lg={10}  >
                                <CustomTextField
                                  fullWidth
                                  value={row[column.id]}
                                  onChange={(e) => {
                                    const updatedRows = [...stateRows];
                                    updatedRows[index][column.id] = e.target.value;
                                    setStateRows(updatedRows);
                                  }}
                                  style={{ marginLeft: 5 }}
                                  inputProps={{ maxLength: 100 }}
                                />

                              </Grid>
                            </Grid>
                          ) : column.typeEdit && isRowExpanded ? (
                            <NumericFormat
                              customInput={CustomTextField}
                              value={row[column.id]?.replace(/\$/g, '')}
                              thousandSeparator
                              decimalScale={2}
                              fixedDecimalScale={false}
                              allowNegative={true}
                              placeholder="0.00"
                              onBlur={() => handleRowBlur(row.id)}
                              onValueChange={(values) => {
                                handleRowEdit(row.id, column.id, values.value);
                              }}
                              isAllowed={(values) => {
                                const { floatValue } = values;
                                return (
                                  floatValue === undefined ||
                                  floatValue <= constants.MAX_AMOUNT_ALLOWED
                                );
                              }}
                              inputProps={{
                                style: {
                                  textAlign: "right",
                                  paddingLeft: 0,
                                  marginRight: 0.1,
                                },
                                onPaste: (e: any) => e.preventDefault(),
                              }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                style: { textAlign: 'right' },
                              }}
                              sx={{ marginLeft: 0.5, pl: 0, width: "50%" }}
                              readOnly={readOnlyForecast}
                            />

                          ) : (
                            row[column.id]
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                    {
                      pageType !== "BudgetAccountDetails" && (
                        <TableRow>
                          <TableCell colSpan={columns.length} sx={{ padding: 0, border: "none" }}>
                            <Collapse in={isRowExpanded} timeout="auto" unmountOnExit>
                              <Grid container spacing={2} sx={{ mx: 0.1 }}>
                                {sortedTransactions.map((mon: any, k: number) => (
                                  <Grid item xs={12} sm={2} lg={0.98} key={k} sx={{ mt: 2, px: 0 }}>
                                    <Tooltip title={formatCurrency(pageType === "BudgetDetails" ? mon?.forecastAmount : mon?.actualAmount)} arrow placement="top">
                                      <Box position="relative" display="inline-block">
                                        <Box position="absolute" top={10} bgcolor="white" zIndex={1}>
                                          {" $ "}
                                        </Box>
                                        <NumericFormat
                                          customInput={CustomTextField}
                                          label={mon?.month}
                                          placeholder="0.00"
                                          isAllowed={(values) => {
                                            const { value, floatValue } = values;
                                            if (floatValue === undefined) return true;
                                            if (Number.isNaN(floatValue)) return false;
                                            if (floatValue > constants.MAX_AMOUNT_ALLOWED) return false;
                                            const [intPart = "", decPart = ""] = (value ?? "").split(".");
                                            const intDigits = intPart.replace("-", "").length;
                                            if (intDigits > 8) return false;
                                            if (decPart.length > 2) return false;
                                            return true;
                                          }}
                                          value={
                                            editingForecasts?.[row.id]?.[mon?.month] ??
                                            ((pageType === "BudgetDetails"
                                              ? mon?.forecastAmount
                                              : mon?.actualAmount) ?? "")
                                          }
                                          onChange={(e) => {
                                            handleForecastChange(mon?.month, row, e.target.value);
                                          }}
                                          onBlur={() =>
                                            handleForecastBlur(mon?.month, editingForecasts?.[row.id]?.[mon?.month], row.id)
                                          }
                                          inputProps={{
                                            style: { textAlign: "right", paddingLeft: 0, marginRight: 0.1 },
                                            onPaste: (e: any) => e.preventDefault()
                                          }}
                                          sx={{ marginLeft: 0.5, pl: 0 }}
                                          readOnly={
                                            pageType === "BudgetDetails" ? readOnlyForecast : mon.readOnly
                                          }
                                          thousandSeparator
                                          decimalScale={2}
                                          fixedDecimalScale={false}
                                          allowNegative={true}
                                        />

                                      </Box>
                                    </Tooltip>
                                  </Grid>
                                ))}
                                <Grid item xs={12}>
                                  <CustomTextField
                                    label={translate('notes')}
                                    readOnly={
                                      pageType === "BudgetDetails"
                                        ? readOnlyForecast
                                        : row.subChartAccountsTransactions[0].readOnly
                                    }
                                    multiline
                                    rows={2}
                                    inputProps={{ maxLength: 250 }}
                                    sx={{ width: '75%', ml: 18, mb: 2 }}
                                    value={
                                      pageType === "ActualsDetails"
                                        ? row.subChartAccountsNotesVO?.actualNotes
                                        : pageType === "BudgetDetails"
                                          ? row.subChartAccountsNotesVO?.budgetNotes
                                          : ''
                                    }
                                    onChange={(e) => handleNotes(row, e.target.value)}
                                    onBlur={(e) => handleNotesBlur(row, e.target.value)}
                                  />
                                </Grid>
                              </Grid>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )
                    }
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
          {pageType === "BudgetAccountDetails" && (
            <TableRow>
              <TableCell align="center">
                <Grid container direction="row">
                  <Grid item lg={10.2}>
                    <CustomTextField
                      fullWidth
                      value={accountNumber}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        const isValid = /^([a-zA-Z0-9.-]*)$/.test(newValue);
                        if (isValid && newValue.length <= 16) {
                          setAccountNumber(newValue);
                        }
                      }}
                      inputProps={{ maxLength: 16, inputMode: "text", pattern: "[a-zA-Z0-9.-]*" }}
                      error={!accountNumber && stateRows[stateRows.length - 1]?.validationError}
                      helperText={!accountNumber && stateRows[stateRows.length - 1]?.validationError ? translate('subChartAccountIsRequired') : ""}
                    />
                  </Grid>
                </Grid>
              </TableCell>
              <TableCell align="left">
                <Grid container direction="row">
                  <Grid item lg={10.2}>
                    <CustomTextField
                      fullWidth
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      inputProps={{ maxLength: 100 }}
                      error={!description && stateRows[stateRows.length - 1]?.validationError}
                      helperText={!description && stateRows[stateRows.length - 1]?.validationError ? translate('descriptionIsRequired') : ""}
                    />
                  </Grid>
                </Grid>
              </TableCell>

              <TableCell align="center" sx={{ fontSize: 16, paddingRight: 2 }} onClick={() => handleAddSubChartAccountRow(stateMainRow ?? null)}>
                <ControlPointIcon sx={{ mb: "-5px", cursor: "pointer" }} /> {translate("subChartAccount")}
              </TableCell>
            </TableRow>
          )}
        </StyledTable>
      </TableContainer>
    </div >
  );
};

export default ReusableSubChartsAccountTable;